import { FunctionComponent, forwardRef } from "react";

import Check from "@uui/Check";
import ThumbsUp from "@uui/ThumbsUp";
import X from "@uui/X";

import ArrowDropDownRounded from "@mui/icons-material/ArrowDropDownRounded";
import LoadingButton, { LoadingButtonProps } from "@mui/lab/LoadingButton";
import styled from "@mui/material/styles/styled";

import type { RecommendationActionType } from "@app/analytics/Recommendations/types";

import Stack, { Row, StackProps } from "@rui/Stack";

const StackWithRef = forwardRef<HTMLDivElement | null, StackProps>(
  (props, ref) => <Stack ref={ref} {...props} />
);
StackWithRef.displayName = "StackWithRef";

export const Container = styled(StackWithRef)(({ theme }) => ({
  backgroundColor: "#FFF",
  borderRadius: "4px",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
}));

export const Heading = styled(Row)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  height: "56px",
  borderBottom: `1px solid ${theme.palette.shades.neutral[100]}`,
}));

export const Content = styled(Row)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const KeyFactor = styled(
  ({ children, hideArrow, ...props }: StackProps & { hideArrow?: boolean }) => (
    <Row spacing={0.5} variant="left" {...props}>
      {!hideArrow && (
        <ArrowDropDownRounded sx={{ color: "shades.negative.500" }} />
      )}
      <span>{children}</span>
    </Row>
  )
)(({ theme, hideArrow }) => ({
  color: theme.palette.shades.neutral[500],
  fontSize: "0.875rem",
  marginLeft: hideArrow ? 0 : theme.spacing(-1),
}));

type ActionButtonProps = LoadingButtonProps & {
  compact?: boolean;
  kind: RecommendationActionType;
};

const ICON_FOR_KIND = {
  accept: <Check />,
  reject: <X />,
  acknowledge: <ThumbsUp />,
};

export const ActionButton = styled<FunctionComponent<ActionButtonProps>>(
  ({ compact: _, kind, ...props }) => (
    <LoadingButton
      variant="outlined"
      {...props}
      startIcon={ICON_FOR_KIND[kind]}
    />
  )
)(({ compact, disabled, kind, theme }) => ({
  height: "40px",
  borderRadius: "6px",
  borderColor: theme.palette.shades.neutral[50],
  color: theme.palette.shades.neutral[600],
  padding: "10px 16px",
  "& .MuiSvgIcon-root:not(.recommendation-action__expand-icon)": {
    color:
      kind === "accept"
        ? theme.palette.shades.positive[600]
        : kind === "acknowledge"
          ? theme.palette.shades.secondary[500]
          : theme.palette.error.main,
    opacity: disabled ? "0.5" : "1",
  },
  "&:hover": {
    backgroundColor:
      kind === "accept"
        ? theme.palette.shades.positive[50]
        : kind === "acknowledge"
          ? theme.palette.shades.secondary[50]
          : theme.palette.shades.negative[50],
    borderColor:
      kind === "accept"
        ? theme.palette.shades.positive[50]
        : kind === "acknowledge"
          ? theme.palette.shades.secondary[50]
          : theme.palette.shades.negative[50],
  },
  ...(compact && {
    minWidth: "fit-content",
    "& .MuiButton-startIcon": {
      margin: 0,
    },
  }),
}));
